import React from 'react';
import { motion } from 'framer-motion';
import { Users, Check, ArrowRight, TrendingUp, ArrowLeft, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import useMetaTags from '../hooks/useMetaTags';
import useStructuredData from '../hooks/useStructuredData';
import { NavbarHTAG } from '../components/NavbarHTAG';
import { FooterHTAG } from '../components/FooterHTAG';

export const CoachingService = () => {
  // Meta tags SEO
  useMetaTags({
    title: 'Coaching Digital & Formation IT en Ardennes | HTAG Facility',
    description: 'Formation digital et coaching pour équipes. Amélioration de performance, gestion de projet, transformation numérique. Services en Ardennes, Château-Porcien. Assistance numérique personnalisée.',
    keywords: 'coaching digital, formation IT, transformation numérique, gestion équipe, Ardennes, Château-Porcien, formation professionnelle',
    url: 'https://htagfacility.fr/services/coaching',
    image: 'https://htagfacility.fr/og-service-coaching.png',
    type: 'article'
  });

  // Structured Data - Service
  useStructuredData({
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Coaching Digital & Formation Équipe',
    'provider': {
      '@type': 'LocalBusiness',
      'name': 'HTAG Facility',
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': 'Château-Porcien',
        'addressLocality': 'Château-Porcien',
        'addressRegion': 'Ardennes',
        'postalCode': '08320',
        'addressCountry': 'FR'
      }
    },
    'description': 'Formation et coaching digital pour améliorer la performance des équipes et transformer les processus',
    'areaServed': ['FR-08', 'Ardennes', 'Grand Est'],
    'offers': {
      '@type': 'Offer',
      'description': 'Service de coaching digital et formation équipe'
    }
  });

  return (
    <div className="bg-[#181C25] text-white lg:ml-16">
      <NavbarHTAG />

      {/* Bouton retour */}
      <div className="relative z-10">
        <div className="container mx-auto mb-8">
          <Link
            to="/?scroll=services"
            className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors font-semibold hover:translate-x-1 duration-300"
          >
            <ArrowLeft size={18} />
            <span>Retour aux services</span>
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="pb-24 bg-gradient-to-b from-[#212635] to-[#181C25]">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            {/* Contenu */}
            <div>
              <h1 className="text-5xl lg:text-6xl font-montserrat font-bold mb-6">
                Performance & Gestion <span className="text-purple-400">d'Équipe</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Optimisez la performance de votre équipe par une meilleure organisation, un management efficace et les bons outils. <strong className="text-gray-300">Ateliers et coaching en présentiel ou visio selon vos préférences.</strong>
              </p>
              <a
                href="/?scroll=contact"
                className="inline-flex items-center space-x-3 px-8 py-4 rounded-lg bg-purple-600 hover:bg-purple-700 font-montserrat font-semibold transition-all"
              >
                <span>Optimiser mon équipe</span>
                <ArrowRight size={20} />
              </a>
            </div>

            {/* Visuel */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative h-80 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-2 border-purple-500/30 overflow-hidden flex items-center justify-center"
            >
              <motion.div
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 2.5, repeat: Infinity }}
                className="text-center"
              >
                <Users size={96} className="text-purple-400 mb-4 mx-auto" />
                <p className="text-gray-300 font-montserrat">Croissance d'Équipe</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services détails */}
      <section className="py-24">
        <div className="container mx-auto">
          {/* Les trois piliers */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-24"
          >
            <h2 className="text-4xl font-montserrat font-bold mb-12 text-white">
              Les 3 piliers de la performance
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: TrendingUp,
                  title: 'Organisation',
                  desc: 'Structure claire, process optimisés, responsabilités définies'
                },
                {
                  icon: Users,
                  title: 'Management',
                  desc: 'Leadership coaching, gestion du stress, communication efficace'
                },
                {
                  icon: Check,
                  title: 'Outils & Méthodes',
                  desc: 'Méthodologies agiles, collaboration, productivité, formation'
                },
              ].map((pillar, idx) => {
                const Icon = pillar.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="spotlight-card p-8 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 hover:border-purple-500 transition-all"
                  >
                    <Icon className="w-12 h-12 text-purple-500 mb-4" />
                    <h3 className="font-montserrat font-bold text-white text-lg mb-3">
                      {pillar.title}
                    </h3>
                    <p className="text-gray-400">{pillar.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Services détails */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-24"
          >
            <h2 className="text-4xl font-montserrat font-bold mb-12 text-white">
              Les missions accompagnées
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  title: 'Audit & Diagnostic',
                  items: [
                    'Audit organisationnel complet',
                    'Interview manager et équipes',
                    'Identification des frictions',
                    'Analyse des process existants',
                    'Rapport d\'analyse détaillé'
                  ]
                },
                {
                  title: 'Coaching Management',
                  items: [
                    'Coaching individual 1:1',
                    'Atelier leadership',
                    'Gestion des conflits',
                    'Communication non-violente',
                    'Développement personnel'
                  ]
                },
                {
                  title: 'Réorganisation',
                  items: [
                    'Restructuration hiérarchique',
                    'Clarification des rôles',
                    'Définition de KPI',
                    'Mise en place de gouvernance',
                    'Transition et accompagnement'
                  ]
                },
                {
                  title: 'Ateliers & Formation',
                  items: [
                    'Atelier productivité équipe',
                    'Méthodologie agile/Scrum',
                    'Outils collaboratifs formation',
                    'Amélioration continue',
                    'Team building thématique'
                  ]
                },
              ].map((service, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-8 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 hover:border-purple-500 transition-all"
                >
                  <h3 className="font-montserrat font-bold text-lg text-purple-400 mb-4">
                    {service.title}
                  </h3>
                  <ul className="space-y-3">
                    {service.items.map((item, i) => (
                      <li key={i} className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-purple-500 flex-shrink-0 mt-1" />
                        <span className="text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Résultats attendus */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-24"
          >
            <h2 className="text-4xl font-montserrat font-bold mb-12 text-white">
              Les résultats attendus
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                '✓ Augmentation de la productivité (+20-30%)',
                '✓ Réduction du turn-over collaborateurs',
                '✓ Meilleure communication interne',
                '✓ Clarté sur les objectifs et KPI',
                '✓ Équipe plus motivée et engagée',
                '✓ Processus plus optimisés',
                '✓ Better work-life balance',
                '✓ Culture d\'amélioration continue'
              ].map((result, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className="p-4 rounded-lg hover:bg-purple-500/10 transition-all border-l-2 border-purple-500"
                >
                  <span className="text-gray-300 font-semibold">{result}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Coaching en Ardennes */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="mb-24 p-12 rounded-2xl bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30"
          >
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="text-purple-400" size={28} />
                  <h3 className="text-2xl font-montserrat font-bold text-white">
                    Formation & Coaching en Ardennes
                  </h3>
                </div>
                <p className="text-gray-300 mb-4">
                  HTAG Facility dispense coaching digital et formations pour PME, associations et collectivités en Ardennes. Basé à <strong>Château-Porcien</strong>, nous adaptons nos programmes de formation à vos besoins spécifiques et vos objectifs de transformation numérique.
                </p>
                <p className="text-gray-400 text-sm">
                  <strong>Format flexible :</strong> Sessions en visio, ateliers sur site, ou format hybride selon vos préférences.
                </p>
              </div>
              <div className="flex-shrink-0">
                <div className="text-center p-6 rounded-xl bg-purple-600/10 border border-purple-500/30">
                  <p className="text-purple-400 font-montserrat font-bold text-sm mb-2">FORMATION RÉGIONALE</p>
                  <p className="text-white font-bold text-lg">Ardennes</p>
                  <p className="text-gray-400 text-sm">Grand Est, France</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA Final */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center p-12 rounded-2xl bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30"
          >
            <h3 className="text-3xl font-montserrat font-bold mb-4 text-white">
              Prêt à libérer le potentiel de votre équipe ?
            </h3>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Discutez avec Eric de votre diagnostic initial. Ensemble, construisons une équipe performante et motivée.
            </p>
            <a
              href="/?scroll=contact"
              className="inline-flex items-center space-x-3 px-10 py-4 rounded-lg bg-purple-600 hover:bg-purple-700 font-montserrat font-semibold transition-all"
            >
              <span>Discuter du coaching</span>
              <ArrowRight size={20} />
            </a>
          </motion.div>
        </div>
      </section>

      <FooterHTAG />
    </div>
  );
};

export default CoachingService;
